import { BreakpointObserver } from '@angular/cdk/layout';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

import { MaterialBottomSheetConfirmation } from '../../business/material-bottom-sheet/material-bottom-sheet-confirmation';
import { MaterialBottomSheetLink } from '../../business/material-bottom-sheet/material-bottom-sheet-link';
import { MaterialBottomSheetOffer } from '../../business/material-bottom-sheet/material-bottom-sheet-offer';
import { MaterialSpinnerComponent } from '../material-spinner/material-spinner.component';
import { MaterialBottomSheetLinkItemsComponent } from './link-items/material-bottom-sheet-link-items.component';
import { MaterialBottomSheetOfferSectionComponent } from './offer-section/material-bottom-sheet-offer-section.component';
import { MaterialBottonSheetQrCodeComponent } from './qr-code/material-botton-sheet-qr-code.component';

@Component({
    selector: 'ecm-mat-bottom-sheet',
    templateUrl: 'material-bottom-sheet.html',
    styleUrls: ['material-bottom-sheet.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgbModule,
        MaterialBottomSheetLinkItemsComponent,
        MaterialBottomSheetOfferSectionComponent,
        MaterialBottonSheetQrCodeComponent,
        MaterialSpinnerComponent,
        NgClass,
        TranslateModule,
        NgTemplateOutlet,
    ],
})
export class MaterialBottomSheetComponent implements OnInit, OnDestroy {
    private readonly modalService = inject(NgbModal);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);

    @ViewChild('mobileOffcanvas') mobileOffcanvas: ElementRef<HTMLDivElement>;
    @ViewChild('webModal') webModal: TemplateRef<any>;

    public linkItems? = input<MaterialBottomSheetLink[]>([]);
    public offerSection? = input<MaterialBottomSheetOffer>(null);
    public displaySnack = input<boolean>(false);
    public qrCodeUrl = input<string>('');
    public openModalIsBottom = input<boolean>(false);
    public openModalTitle = input<string>('Ouvrir la popin');
    public openModalIcon = input<string>('icon-assistance');
    public openModalColor = input<string>('');
    public e2e = input<string>('e2e-mat-bottom-sheet');
    public hasCloseBottomModalIsBottom = input<boolean>(false);
    public confirmation = input<MaterialBottomSheetConfirmation>(null);
    public isLoading = input<boolean>(false);
    public isError = input<boolean>(false);
    public errorTitle = input<string>('');
    public errorText = input<string>('');
    public isVerticalLayout = input<boolean>(false);
    public isSosh = input<boolean>(false);
    public clickLink = output<MaterialBottomSheetLink>();
    public closeModal = output<any>();
    public openModalEvent = output<void>();
    public confirmAction = output<void>();
    public cancelAction = output<void>();

    public isXsScreen = false;
    public isOffcanvasOpen = false;
    public i18nPath = 'material.bottomSheet';
    public MOBILE_WIDTH = '(max-width: 735px)';

    // Propriétés pour le swipe
    private touchStartY = 0;
    private touchCurrentY = 0;
    private isDragging = false;
    private readonly SWIPE_THRESHOLD = 50; // Distance minimale pour déclencher la fermeture

    public ngOnInit() {
        this.breakpointObserver
            .observe([this.MOBILE_WIDTH])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                this.isXsScreen = result.matches;
            });
    }

    public ngOnDestroy() {
        // Nettoyage si le composant est détruit alors que le modal est ouvert
        this.unblockBodyScroll();
    }

    public get withLinks(): boolean {
        return this.linkItems()?.length > 0;
    }

    public get withOfferSection(): boolean {
        return !!this.offerSection();
    }

    public get withQrCode(): boolean {
        return !!this.qrCodeUrl();
    }

    public get withConfirmation(): boolean {
        return !!this.confirmation();
    }

    public onPrimaryButtonClick(): void {
        this.confirmAction.emit();
        this.closeWebModal();
    }

    public onSecondaryButtonClick(): void {
        this.cancelAction.emit();
        this.closeWebModal();
    }

    public onPrimaryButtonClickMobile(): void {
        this.confirmAction.emit();
        this.closeMobileModal();
    }

    public onSecondaryButtonClickMobile(): void {
        this.cancelAction.emit();
        this.closeMobileModal();
    }

    public openModal(content: TemplateRef<any> | null): void {
        this.openModalEvent.emit();
        if (this.isXsScreen) {
            this.openMobileModal();
        } else {
            this.openWebModal(content);
        }
    }

    public open(): void {
        this.openModalEvent.emit();
        const isMobile = this.breakpointObserver.isMatched(this.MOBILE_WIDTH);
        if (isMobile) {
            this.openMobileModal();
        } else {
            this.openWebModal(this.webModal);
        }
    }

    public openWebModal(content: TemplateRef<any>) {
        const windowClass = `apply-one-style bg-transparent${this.isSosh() ? ' ecm-theme-sosh' : ''}`;
        this.modalService.open(content, { windowClass });
    }

    public closeWebModal() {
        this.modalService.dismissAll();
        this.closeModal.emit(true);
    }

    public openMobileModal() {
        this.isOffcanvasOpen = true;
        this.blockBodyScroll();
        this.cdr.markForCheck();
        setTimeout(() => {
            this.mobileOffcanvas?.nativeElement?.focus();
        }, 100);
    }

    public closeMobileModal() {
        this.isOffcanvasOpen = false;
        this.unblockBodyScroll();
        this.closeModal.emit(true);
        this.cdr.markForCheck();
    }

    private blockBodyScroll() {
        document.body.classList.add('overflow-hidden');
    }

    private unblockBodyScroll() {
        document.body.classList.remove('overflow-hidden');
    }

    public onBackdropKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.closeMobileModal();
        }
    }

    public onOffcanvasKeyDown(event: KeyboardEvent): void {
        // Exemple : fermer la modale avec la touche Échap
        if (event.key === 'Escape' || event.key === 'Esc') {
            this.closeMobileModal();
        }
    }

    public onSwipeHandleTouchStart(event: TouchEvent): void {
        this.touchStartY = event.touches[0].clientY;
        this.isDragging = true;
    }

    public onSwipeHandleTouchMove(event: TouchEvent): void {
        if (!this.isDragging) return;

        this.touchCurrentY = event.touches[0].clientY;
        const deltaY = this.touchCurrentY - this.touchStartY;

        // Permettre le glissement vers le bas uniquement
        if (deltaY > 0) {
            event.preventDefault();
        }
    }

    public onSwipeHandleTouchEnd(): void {
        if (!this.isDragging) return;

        const deltaY = this.touchCurrentY - this.touchStartY;

        // Si le glissement vers le bas dépasse le seuil, fermer le modal
        if (deltaY > this.SWIPE_THRESHOLD) {
            this.closeMobileModal();
        }

        // Réinitialiser les valeurs
        this.isDragging = false;
        this.touchStartY = 0;
        this.touchCurrentY = 0;
    }
}
